import { useState, useRef, useEffect } from "react";
import { Code, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * CodeInputRenderer — Editable code input with monospace styling
 * 
 * Expected item shape:
 * {
 *   type: "code_input",
 *   id: "q1_code",
 *   language: "python" | "javascript" | "sql" | etc,
 *   placeholder: "Write your solution here...",
 *   starter_code: "def solve(arr):\n    # Your code here\n    pass" (optional),
 *   title: "Your Solution" (optional),
 *   min_lines: 10 (optional)
 * }
 */
export function CodeInputRenderer({ item, index, responses, onResponseChange, onPaste }) {
  const id = item.id || `code_input_${index}`;
  const language = item.language || "text";
  const placeholder = item.placeholder || "Write your code here...";
  const starterCode = item.starter_code || "";
  const title = item.title || "Your Code";
  const minLines = item.min_lines || 12;

  const textareaRef = useRef(null);
  const [lineCount, setLineCount] = useState(minLines);

  const value = responses[id] !== undefined ? responses[id] : starterCode;

  useEffect(() => {
    const lines = (value || "").split("\n").length;
    setLineCount(Math.max(lines + 2, minLines));
  }, [value, minLines]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newValue = value.substring(0, start) + "    " + value.substring(end);
      onResponseChange(id, newValue);
      // Restore cursor position after React re-renders
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 4;
      });
    }
  };

  const handleReset = () => {
    onResponseChange(id, starterCode);
  };

  const currentLines = (value || "").split("\n");

  return (
    <div key={index} className="my-4" data-testid={`code-input-${index}`}>
      {/* Header */}
      <div className="flex items-center justify-between bg-[#1a1a2e] text-gray-300 px-4 py-2 rounded-t-lg border border-b-0 border-[#2a2a3e]">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-medium text-gray-200">{title}</span>
          <span className="text-xs bg-[#2a2a3e] px-2 py-0.5 rounded text-gray-400 uppercase tracking-wide">
            {language}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {currentLines.length} lines
          </span>
          {starterCode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-6 px-2 text-xs text-gray-400 hover:text-white hover:bg-[#2a2a3e]"
              data-testid={`code-input-reset-${index}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
          )}
        </div>
      </div>
      {/* Editor area */}
      <div className="flex bg-[#1e1e2e] rounded-b-lg border border-t-0 border-[#2a2a3e] overflow-hidden">
        {/* Line numbers */}
        <div className="select-none text-right text-gray-600 text-xs leading-6 py-3 pl-3 pr-2 flex-shrink-0 border-r border-[#2a2a3e]">
          {Array.from({ length: Math.max(lineCount, currentLines.length) }, (_, i) => (
            <div key={i} className="h-6">{i + 1}</div>
          ))}
        </div>
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onResponseChange(id, e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => {
            const pasted = e.clipboardData?.getData("text") || "";
            if (pasted && onPaste) onPaste(id, pasted);
          }}
          placeholder={placeholder}
          spellCheck={false}
          className="flex-1 bg-transparent text-gray-200 font-mono text-[13px] leading-6 p-3 resize-none outline-none placeholder:text-gray-600 min-h-[200px]"
          style={{ minHeight: `${lineCount * 24 + 24}px` }}
          data-testid={`code-input-textarea-${index}`}
        />
      </div>
      <p className="text-xs text-gray-400 mt-1 ml-1">
        Tab key inserts 4 spaces. Your code auto-saves.
      </p>
    </div>
  );
}
